/**
 * Creature Survey - Visual reflection for ages 4-6
 * Emoji-based feedback, no text required
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveSurveyResponse, prepareSurveyData } from '../../lib/surveyOperations';
import type { CreatureType, SuperpowerCard } from './types';

interface CreatureSurveyProps {
  language: 'en' | 'es';
  onComplete: () => void;
  creatureType?: CreatureType;
  creatureName?: string;
  selectedPowers?: SuperpowerCard[];
  gameDurationSeconds?: number;
}

const TEXT = {
  en: {
    title: 'How did you feel?',
    question1: 'How do you feel now?',
    question2: 'What was your favorite part?',
    thankYou: 'Thank you for sharing!',
    playAgain: 'Play Again',
    goHome: 'Home',
    options1: [
      { emoji: '😊', label: 'Happy' },
      { emoji: '😍', label: 'Excited' },
      { emoji: '🤔', label: 'Thoughtful' },
      { emoji: '😌', label: 'Calm' },
      { emoji: '🤩', label: 'Amazed' }
    ],
    options2: [
      { emoji: '🎤', label: 'Voice Drawing' },
      { emoji: '🐉', label: 'Choosing Creature' },
      { emoji: '⚡', label: 'Superpowers' },
      { emoji: '✏️', label: 'Drawing' },
      { emoji: '✨', label: 'Everything!' }
    ]
  },
  es: {
    title: '¿Cómo te sentiste?',
    question1: '¿Cómo te sientes ahora?',
    question2: '¿Cuál fue tu parte favorita?',
    thankYou: '¡Gracias por compartir!',
    playAgain: 'Jugar de Nuevo',
    goHome: 'Inicio',
    options1: [
      { emoji: '😊', label: 'Feliz' },
      { emoji: '😍', label: 'Emocionado' },
      { emoji: '🤔', label: 'Pensativo' },
      { emoji: '😌', label: 'Tranquilo' },
      { emoji: '🤩', label: 'Asombrado' }
    ],
    options2: [
      { emoji: '🎤', label: 'Dibujo con Voz' },
      { emoji: '🐉', label: 'Elegir Criatura' },
      { emoji: '⚡', label: 'Superpoderes' },
      { emoji: '✏️', label: 'Dibujar' },
      { emoji: '✨', label: '¡Todo!' }
    ]
  }
};

export default function CreatureSurvey({
  language,
  onComplete,
  creatureType,
  creatureName,
  selectedPowers,
  gameDurationSeconds
}: CreatureSurveyProps) {
  const [step, setStep] = useState<1 | 2 | 'complete'>(1);
  const [selectedFeeling, setSelectedFeeling] = useState<string | null>(null);
  const [selectedFeelingLabel, setSelectedFeelingLabel] = useState<string | null>(null);
  const [selectedFavorite, setSelectedFavorite] = useState<string | null>(null);
  const [selectedFavoriteLabel, setSelectedFavoriteLabel] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const t = TEXT[language];

  const handleFeelingSelect = (emoji: string, label: string) => {
    setSelectedFeeling(emoji);
    setSelectedFeelingLabel(label);
    setTimeout(() => setStep(2), 800);
  };

  const handleFavoriteSelect = async (emoji: string, label: string) => {
    setSelectedFavorite(emoji);
    setSelectedFavoriteLabel(label);
    setIsSaving(true);

    // Save to database
    if (selectedFeeling && selectedFeelingLabel) {
      const powerIds = selectedPowers?.map(p => p.id) || [];
      const surveyData = prepareSurveyData(
        selectedFeeling,
        selectedFeelingLabel,
        emoji,
        label,
        language,
        creatureType,
        creatureName,
        powerIds,
        gameDurationSeconds
      );

      const result = await saveSurveyResponse(surveyData);

      if (result.success) {
        console.log('Creature survey saved successfully');
      } else {
        console.error('Failed to save creature survey:', result.error);
      }
    }

    setIsSaving(false);
    setTimeout(() => setStep('complete'), 800);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center p-6">
      <div className="max-w-4xl w-full">
        <AnimatePresence mode="wait">
          {/* STEP 1: How do you feel? */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="text-center"
            >
              <h2
                className="text-4xl md:text-6xl mb-12"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 300,
                  color: '#2a6d5b',
                  letterSpacing: '-0.015em',
                }}
              >
                {t.question1}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {t.options1.map((option, index) => {
                  const active = selectedFeeling === option.emoji;
                  return (
                    <motion.button
                      key={option.emoji}
                      initial={{ scale: 0.92, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        delay: index * 0.06,
                        type: 'spring',
                        stiffness: 200,
                      }}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => handleFeelingSelect(option.emoji, option.label)}
                      style={{
                        padding: 14,
                        background: '#ffffff',
                        border: active ? '2px solid #42a57a' : '1px solid #e8e0c8',
                        borderRadius: 18,
                        cursor: 'pointer',
                        transition: 'border-color 200ms ease',
                      }}
                    >
                      <div
                        style={{
                          background: '#dcecda',
                          borderRadius: 14,
                          padding: '24px 12px 18px',
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ fontSize: 72, lineHeight: 1 }}>{option.emoji}</div>
                      </div>
                      <p
                        style={{
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontStyle: 'italic',
                          fontSize: 20,
                          fontWeight: 600,
                          color: '#2a6d5b',
                        }}
                      >
                        {option.label}
                      </p>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Favorite part */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="text-center"
            >
              <h2
                className="text-4xl md:text-6xl mb-12"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 300,
                  color: '#2a6d5b',
                  letterSpacing: '-0.015em',
                }}
              >
                {t.question2}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {t.options2.map((option, index) => {
                  const active = selectedFavorite === option.emoji;
                  // cycle pastel palette so step 2 differs visually from step 1
                  const pastels = ['#dbeef5', '#fce8d2', '#fadcdc', '#e8defc', '#d6f0e0'];
                  const pastel = pastels[index % pastels.length];
                  return (
                    <motion.button
                      key={option.emoji}
                      initial={{ scale: 0.92, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        delay: index * 0.06,
                        type: 'spring',
                        stiffness: 200,
                      }}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => handleFavoriteSelect(option.emoji, option.label)}
                      style={{
                        padding: 14,
                        background: '#ffffff',
                        border: active ? '2px solid #42a57a' : '1px solid #e8e0c8',
                        borderRadius: 18,
                        cursor: 'pointer',
                        transition: 'border-color 200ms ease',
                      }}
                    >
                      <div
                        style={{
                          background: pastel,
                          borderRadius: 14,
                          padding: '24px 12px 18px',
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ fontSize: 72, lineHeight: 1 }}>{option.emoji}</div>
                      </div>
                      <p
                        style={{
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontStyle: 'italic',
                          fontSize: 20,
                          fontWeight: 600,
                          color: '#2a6d5b',
                        }}
                      >
                        {option.label}
                      </p>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* COMPLETE: Thank you */}
          {step === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Celebration Animation */}
              <div className="relative mb-8">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                    animate={{
                      scale: [0, 1.5, 1],
                      x: Math.cos((i * 18 * Math.PI) / 180) * 250,
                      y: Math.sin((i * 18 * Math.PI) / 180) * 250,
                      opacity: [1, 1, 0]
                    }}
                    transition={{
                      duration: 2,
                      ease: 'easeOut'
                    }}
                    className="absolute top-1/2 left-1/2 text-6xl"
                    style={{ transform: 'translate(-50%, -50%)' }}
                  >
                    {['⭐', '✨', '🌟', '💫', '🎉'][i % 5]}
                  </motion.div>
                ))}

                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 360]
                  }}
                  transition={{
                    duration: 2,
                    ease: 'easeInOut'
                  }}
                  className="text-9xl"
                >
                  🎉
                </motion.div>
              </div>

              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-5xl md:text-7xl mb-6"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 300,
                  color: '#2a6d5b',
                  letterSpacing: '-0.015em',
                }}
              >
                {t.thankYou}
              </motion.h2>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mb-12"
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 28,
                  color: '#2a6d5b',
                }}
              >
                {selectedFeeling && <span style={{ fontSize: 72, marginRight: 16 }}>{selectedFeeling}</span>}
                {selectedFavorite && <span style={{ fontSize: 72 }}>{selectedFavorite}</span>}
              </motion.p>

              {/* Action Buttons */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '16px 40px',
                    background: '#42a57a',
                    color: '#ffffff',
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 22,
                    fontWeight: 600,
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
                  }}
                >
                  {t.playAgain}
                </button>

                <button
                  onClick={onComplete}
                  style={{
                    padding: '16px 40px',
                    background: 'transparent',
                    color: '#42a57a',
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: 22,
                    fontWeight: 600,
                    borderRadius: 999,
                    border: '1.5px solid #42a57a',
                    cursor: 'pointer',
                  }}
                >
                  {t.goHome}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
